import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../api/client'
import type { GridQuery } from '../api/client'
import { invalidateFleet } from '../api/invalidate'
import { requireAccessToken } from '../auth/msal'
import './BulkRegistrySync.css'

/** The server's own page ceiling — the same one the registration lookup is held to. */
const COLLECT_PAGE_SIZE = 200

type Target = { vehicleId: number; registration: string }
type Failure = Target & { message: string }

type State =
  | { phase: 'idle' }
  | { phase: 'collecting' }
  | { phase: 'confirm'; targets: Target[] }
  | {
      phase: 'running' | 'done'
      targets: Target[]
      completed: number
      reused: number
      failures: Failure[]
      /** Why the run ended early, when it did. Null for a run that went through. */
      stoppedBecause: string | null
    }

/**
 * Responses that mean every remaining call would fail the same way. Carrying on
 * would only fill the failure list with sixty copies of one sentence.
 *
 * 401 — the session lapsed mid-run; 501 — the provider is not configured; 429 —
 * the registry asked us to slow down, and hammering a paid API is the wrong answer.
 */
const STOPS_THE_RUN = new Set([401, 429, 501])

/**
 * "Sync all from RTO" for the vehicles the register is currently showing that have
 * never been synced.
 *
 * One call per vehicle, in sequence, through the same endpoint the per-row button
 * uses — so each vehicle gets the full fetch → stage → apply, the reuse rule that
 * skips a response already held, and the hand-correction guard. A batch path of its
 * own would be a second way of doing the one job, and two paths drift.
 *
 * The bulk sweep is not used, deliberately. It fetches only — parse and apply are
 * separate fleet-wide passes — and it is an operator tool for an onboarding run.
 * This is the everyday case: a few dozen vehicles imported last week.
 *
 * Runs in the browser, so leaving the page stops it. Nothing is lost when that
 * happens: every vehicle already done is fully done, and pressing the button again
 * picks up only those still unsynced, because the filter no longer matches the rest.
 */
export function BulkRegistrySync({
  query,
  total,
  unitCostPaise,
}: {
  /** The register's current filter. Paging is replaced; everything else is kept. */
  query: GridQuery
  /** How many vehicles the filter matches, for the button label before collecting. */
  total: number
  unitCostPaise: number | undefined
}) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<State>({ phase: 'idle' })
  const cancelled = useRef(false)

  // Leaving the page stops the run. A loop carrying on invisibly, spending money
  // with nothing on screen to say so or to stop it, is worse than stopping.
  useEffect(
    () => () => {
      cancelled.current = true
    },
    [],
  )

  async function collect() {
    setState({ phase: 'collecting' })

    try {
      const targets: Target[] = []
      let page = 1
      let totalPages = 1

      do {
        const result = await api.getVehicles({ ...query, page, pageSize: COLLECT_PAGE_SIZE })
        totalPages = result.totalPages ?? 1

        for (const row of result.items ?? []) {
          // Re-checked per row rather than trusting the filter: the button is only
          // offered under "Not RTO synced", but a vehicle that already holds RTO
          // data must never be put in a batch that may charge for it.
          if (row.vehicleId && !row.hasRegistryData)
            targets.push({ vehicleId: row.vehicleId, registration: row.registration ?? '' })
        }

        page++
      } while (page <= totalPages)

      setState(targets.length ? { phase: 'confirm', targets } : { phase: 'idle' })
    } catch (e) {
      setState({
        phase: 'done',
        targets: [],
        completed: 0,
        reused: 0,
        failures: [],
        stoppedBecause: e instanceof Error ? e.message : 'Could not list the vehicles.',
      })
    }
  }

  async function run(targets: Target[]) {
    // At the top of the click, while the popup is still allowed to open — see
    // requireAccessToken. Discovering a stale session sixty calls in is too late.
    try {
      await requireAccessToken()
    } catch (e) {
      setState({
        phase: 'done',
        targets,
        completed: 0,
        reused: 0,
        failures: [],
        stoppedBecause: e instanceof Error ? e.message : 'Sign-in failed.',
      })
      return
    }

    cancelled.current = false
    let completed = 0
    let reused = 0
    const failures: Failure[] = []
    let stoppedBecause: string | null = null

    const publish = (phase: 'running' | 'done') =>
      setState({ phase, targets, completed, reused, failures: [...failures], stoppedBecause })

    publish('running')

    for (const target of targets) {
      if (cancelled.current) {
        stoppedBecause = `Stopped after ${completed} of ${targets.length}.`
        break
      }

      try {
        const result = await api.syncFromRegistry(target.vehicleId)
        if (result.reusedExisting) reused++
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Sync failed.'

        if (e instanceof ApiError && STOPS_THE_RUN.has(e.status)) {
          stoppedBecause = message
          break
        }

        // A 404 or 502 belongs to this vehicle — a registration the RTO rejects,
        // most often — and says nothing about the next one.
        failures.push({ ...target, message })
      }

      completed++
      publish('running')

      // The table, the strip and the dashboard follow along rather than all
      // jumping at the end — and a run cut short has still shown its work.
      if (completed % 10 === 0) void invalidateFleet(queryClient)
    }

    publish('done')
    void invalidateFleet(queryClient)
  }

  const rupees = (count: number) =>
    unitCostPaise === undefined
      ? null
      : `₹${((count * unitCostPaise) / 100).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`

  if (state.phase === 'idle' || state.phase === 'collecting') {
    return (
      <div className="rtobulk">
        <div className="rtobulk__row">
          <p className="rtobulk__msg">
            <b>{total}</b> vehicle{total === 1 ? '' : 's'} here {total === 1 ? 'has' : 'have'}{' '}
            never been confirmed against the RTO.
          </p>
          <div className="rtobulk__actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={state.phase === 'collecting' || total === 0}
              onClick={() => void collect()}
            >
              {state.phase === 'collecting' ? 'Counting…' : `⟳ Sync all ${total}`}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (state.phase === 'confirm') {
    const n = state.targets.length
    const cost = rupees(n)

    return (
      <div className="rtobulk" role="alertdialog" aria-label="Confirm RTO sync">
        <div className="rtobulk__row">
        <p className="rtobulk__msg">
          Sync <b>{n}</b> vehicle{n > 1 ? 's' : ''} from the RTO?
          {cost && (
            <>
              {' '}
              Up to <b>{cost}</b> — a vehicle whose RTO response is already held is
              not charged again.
            </>
          )}
        </p>
        <div className="rtobulk__actions">
          <button type="button" className="btn" onClick={() => setState({ phase: 'idle' })}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void run(state.targets)}
          >
            Sync {n}
          </button>
        </div>
        </div>
      </div>
    )
  }

  const { targets, completed, reused, failures, stoppedBecause } = state
  const succeeded = completed - failures.length
  const charged = succeeded - reused
  const running = state.phase === 'running'
  const pct = targets.length ? Math.round((completed / targets.length) * 100) : 0

  return (
    <div className="rtobulk" role="status" aria-live="polite">
      <div className="rtobulk__row">
        <p className="rtobulk__msg">
          {running ? 'Syncing from the RTO… ' : 'RTO sync finished. '}
          <b>{completed}</b> of <b>{targets.length}</b>
          {succeeded > 0 && <> · {succeeded} synced</>}
          {reused > 0 && <> · {reused} from a held response, not charged</>}
          {!running && charged > 0 && rupees(charged) && <> · {rupees(charged)} spent</>}
          {failures.length > 0 && <> · <span className="rtobulk__bad">{failures.length} failed</span></>}
        </p>
        <div className="rtobulk__actions">
          {running ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                cancelled.current = true
              }}
            >
              Stop
            </button>
          ) : (
            <button type="button" className="btn" onClick={() => setState({ phase: 'idle' })}>
              Dismiss
            </button>
          )}
        </div>
      </div>

      {targets.length > 0 && (
        <div
          className="rtobulk__bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={targets.length}
          aria-valuenow={completed}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
      )}

      {stoppedBecause && <p className="rtobulk__stop">{stoppedBecause}</p>}

      {failures.length > 0 && (
        <ul className="rtobulk__fails">
          {failures.map((f) => (
            <li key={f.vehicleId}>
              <span className="mono">{f.registration}</span> — {f.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
